import { Atom, Waves, Activity, Sparkles, Zap, ShieldCheck } from "lucide-react";
import DNAHelix from "@/components/three/DNAHelix";

const CARDS = [
  {
    icon: Atom,
    title: "GHK-Cu Peptide",
    desc: "X39 elevates the body's natural copper peptide — clinically associated with tissue remodelling, skin renewal & inflammation balance.",
    span: "md:col-span-7",
    accent: "#00F0FF",
  },
  {
    icon: Waves,
    title: "Phototherapy",
    desc: "Specific wavelengths of light stimulate cellular communication non-invasively. No drugs, chemicals or stimulants.",
    span: "md:col-span-5",
    accent: "#D900FF",
  },
  {
    icon: Activity,
    title: "Cellular Resonance",
    desc: "Each patch reflects body heat back into the skin as a precise spectrum that activates dormant signalling pathways.",
    span: "md:col-span-4",
    accent: "#00FF66",
  },
  {
    icon: Sparkles,
    title: "Stem Cell Decline",
    desc: "After 35, stem cell efficiency drops sharply. X39 helps modulate the signal — supporting regeneration where the body needs it most.",
    span: "md:col-span-4",
    accent: "#00F0FF",
  },
  {
    icon: ShieldCheck,
    title: "Drug-free",
    desc: "100% non-transdermal. Nothing enters the body. Only light frequency — making it suitable across ages and lifestyles.",
    span: "md:col-span-4",
    accent: "#D900FF",
  },
];

export default function Science() {
  return (
    <section
      id="science"
      data-testid="science-section"
      className="relative py-24 lg:py-32 bg-[#030508]/55 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="grid md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <span className="text-label">∙ 01 / Science</span>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              The biology of <span className="text-[#00F0FF] glow-cyan">light.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end">
            <p className="text-white/65 leading-relaxed">
              Backed by 20+ years of phototherapy research and over 70 independent
              studies, X39 doesn't add anything to your body — it activates what's
              already there. A single peptide. Twenty four hours of signal.
            </p>
          </div>
        </div>

        {/* Bento + DNA */}
        <div className="grid md:grid-cols-12 gap-6">
          {/* DNA scene card */}
          <div
            data-testid="science-dna-card"
            className="md:col-span-7 glass-card relative h-[420px] md:h-[520px] overflow-hidden group"
          >
            <div className="absolute inset-0">
              <DNAHelix />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-0 inset-x-0 p-8">
              <span className="text-label">DNA / signal pathway</span>
              <h3 className="font-display font-bold text-2xl mt-2">
                Awaken the blueprint inside every cell.
              </h3>
            </div>
            <div className="absolute top-6 right-6 flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-white/50">
              <Zap className="w-3 h-3 text-[#00FF66]" />
              Active
            </div>
          </div>

          {/* First two side cards */}
          <div className="md:col-span-5 grid gap-6">
            {CARDS.slice(0, 2).map((c, i) => (
              <Card key={i} {...c} />
            ))}
          </div>

          {/* lower row */}
          {CARDS.slice(2).map((c, i) => (
            <Card key={i + 2} {...c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Card({ icon: Icon, title, desc, span, accent }) {
  return (
    <div
      data-testid={`science-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`glass-card relative p-7 ${span} min-h-[200px] flex flex-col justify-between`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 grid place-items-center border"
          style={{ borderColor: accent, color: accent }}
        >
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        <span className="text-label">{title}</span>
      </div>
      <p className="mt-6 text-white/75 leading-relaxed text-sm">{desc}</p>
    </div>
  );
}
